/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from 'react';
import { FaMoneyBillWave, FaFileAlt, FaFileContract, FaCheckCircle, FaCreditCard, FaCalendarAlt, FaReceipt, FaClock, FaSyncAlt } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as authService from '../services/authService';
import * as articleService from '../services/articleService';

const MyPayment = () => {
  const [payments, setPayments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    youngIndiaArticles: 0,
    abstracts: 0,
    papers: 0,
    totalAmount: 0
  });
  const [userRole, setUserRole] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      navigate('/login');
      return;
    }

    setUserRole(currentUser.role);
    loadPayments();
  }, [navigate]);

  // Define which payment types are allowed for each role
  const getAllowedPaymentTypes = (role) => {
    switch (role) {
      case 'graduate':
      case 'post-graduate':
        return ['young-india-article']; // Only Young India articles
      case 'phd':
        return ['young-india-article', 'abstract', 'paper']; // Young India articles + Research papers
      case 'professor':
        return ['abstract', 'paper']; // Only abstracts and papers
      default:
        return ['young-india-article', 'abstract', 'paper']; // Show all for admin/user
    }
  };

  // Get grid column class based on role
  const getGridColumns = (role) => {
    switch (role) {
      case 'graduate':
      case 'post-graduate':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'; // 3 columns
      case 'phd':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5'; // 6 columns
      case 'professor':
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-4'; // 4 columns
      default:
        return 'grid-cols-1 md:grid-cols-2 lg:grid-cols-5'; // Default 6 columns
    }
  };

  const loadPayments = async () => {
    setIsLoading(true);
    try {
      const response = await articleService.getMyPayments();
      if (response.success) {
        const backendPayments = response.data;

        // Map backend payment purpose to frontend type
        // Backend sends exact enum strings: SIGNUP_FEE, YI_ARTICLE_FEE, ABSTRACT_FEE, PAPER_FEE, etc.
        const mappedPayments = backendPayments.map(p => {
          let type = 'unknown';
          const purposeRaw = p.purpose || '';
          const purposeLower = purposeRaw.toLowerCase();

          // Match backend enum values (exact) and also descriptive strings (lowercase contains)
          if (
            purposeRaw === 'SIGNUP_FEE' ||
            purposeRaw === 'SIGNUP' ||
            purposeLower.includes('signup') ||
            purposeLower.includes('registration')
          ) {
            type = 'signup-fee';
          } else if (
            purposeRaw === 'YI_ARTICLE_FEE' ||
            purposeRaw === 'YI_ARTICLE' ||
            purposeRaw === 'YOUNG_INDIA_ARTICLE' ||
            purposeLower.includes('running title') ||
            purposeLower.includes('young india') ||
            purposeLower.includes('yi_article') ||
            purposeLower.includes('submission fee')
          ) {
            type = 'young-india-article';
          } else if (
            purposeRaw === 'ABSTRACT_FEE' ||
            purposeRaw === 'ABSTRACT' ||
            purposeLower.includes('abstract')
          ) {
            type = 'abstract';
          } else if (
            purposeRaw === 'PAPER_FEE' ||
            purposeRaw === 'PAPER' ||
            purposeRaw === 'RESEARCH_PAPER' ||
            purposeLower.includes('paper')
          ) {
            type = 'paper';
          }

          return {
            ...p,
            type: type,
            date: p.created_at,
            id: p.razorpay_order_id,
            paymentMethod: 'online'
          };
        });

        setPayments(mappedPayments);

        // Calculate statistics - Only sum PAID amounts for the total
        const youngIndiaArticlePayments = mappedPayments.filter(p => p.type === 'young-india-article' && p.status === 'PAID').length;
        const abstractPayments = mappedPayments.filter(p => p.type === 'abstract' && p.status === 'PAID').length;
        const paperPayments = mappedPayments.filter(p => p.type === 'paper' && p.status === 'PAID').length;
        const totalAmount = mappedPayments
          .filter(p => p.status === 'PAID')
          .reduce((sum, p) => sum + Number(p.amount), 0);

        setStats({
          total: mappedPayments.length,
          youngIndiaArticles: youngIndiaArticlePayments,
          abstracts: abstractPayments,
          papers: paperPayments,
          totalAmount: totalAmount
        });
      }
    } catch (error) {
      console.error('Error loading payments:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSync = async (orderId) => {
    try {
      const response = await articleService.syncPaymentStatus(orderId);
      if (response.success) {
        alert('Payment status updated successfully!');
        loadPayments(); // Reload list
      } else {
        alert(response.message || 'Payment not yet confirmed. Please try again.');
      }
    } catch (error) {
      console.error('Sync error:', error);
      alert(error.message || 'Error syncing payment status.');
    }
  };

  const getPaymentTypeConfig = (type) => {
    switch (type) {
      case 'signup-fee':
        return {
          label: 'Signup / Registration Fee',
          icon: FaMoneyBillWave,
          bgColor: 'bg-indigo-50',
          borderColor: 'border-indigo-500',
          iconColor: 'text-indigo-500'
        };
      case 'young-india-article':
        return {
          label: 'Young India Article',
          icon: FaFileAlt,
          bgColor: 'bg-blue-50',
          borderColor: 'border-blue-500',
          iconColor: 'text-blue-500'
        };
      case 'abstract':
        return {
          label: 'Abstract Submission',
          icon: FaFileContract,
          bgColor: 'bg-purple-50',
          borderColor: 'border-purple-500',
          iconColor: 'text-purple-500'
        };
      case 'paper':
        return {
          label: 'Research Paper',
          icon: FaFileAlt,
          bgColor: 'bg-green-50',
          borderColor: 'border-green-500',
          iconColor: 'text-green-500'
        };
      default:
        return {
          label: 'General Payment',
          icon: FaReceipt,
          bgColor: 'bg-gray-50',
          borderColor: 'border-gray-400',
          iconColor: 'text-gray-500'
        };
    }
  };

  const getPaymentMethodIcon = (method) => {
    switch (method) {
      case 'online':
        return <FaCreditCard className="text-indigo-500" />;
      case 'card':
        return <FaCreditCard className="text-indigo-500" />;
      case 'upi':
        return <FaMoneyBillWave className="text-green-500" />;
      default:
        return <FaMoneyBillWave className="text-gray-500" />;
    }
  };

  // Check if a stat card should be displayed based on role
  const shouldShowStatCard = (type) => {
    const allowedTypes = getAllowedPaymentTypes(userRole);
    return allowedTypes.includes(type);
  };

  const darkGradient = {
    background: 'linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(49, 46, 129) 50%, rgb(15, 23, 42) 100%)'
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-cyan-50 to-indigo-50 py-6">
      <div className="max-w mx-auto px-4">
        {/* Header */}
        <div className="rounded-2xl shadow-lg p-6 mb-6" style={darkGradient}>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-white mb-2">My Payments</h1>
              <p className="text-blue-100">View all your payment transactions</p>
            </div>
            <div className="hidden md:block">
              <div className="w-16 h-16 bg-white/20 rounded-xl flex items-center justify-center">
                <FaMoneyBillWave className="text-3xl text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Statistics Cards - Dynamic Grid Based on Role */}
        <div className={`grid ${getGridColumns(userRole)} gap-4 mb-6`}>
          {/* Total Payments */}
          <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-indigo-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600 mb-1">Total Payments</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <FaReceipt className="text-2xl text-indigo-500" />
              </div>
            </div>
          </div>

          {/* Young India Articles - Only for graduate, post-graduate, and phd */}
          {shouldShowStatCard('young-india-article') && (
            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Young India Articles</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.youngIndiaArticles}</p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                  <FaFileAlt className="text-2xl text-blue-500" />
                </div>
              </div>
            </div>
          )}

          {/* Abstracts - Only for professor */}
          {shouldShowStatCard('abstract') && (
            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-purple-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Abstracts</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.abstracts}</p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                  <FaFileContract className="text-2xl text-purple-500" />
                </div>
              </div>
            </div>
          )}

          {/* Papers - For phd and professor */}
          {shouldShowStatCard('paper') && (
            <div className="bg-white rounded-2xl shadow-lg p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 mb-1">Research Papers</p>
                  <p className="text-3xl font-bold text-gray-900">{stats.papers}</p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <FaFileAlt className="text-2xl text-green-500" />
                </div>
              </div>
            </div>
          )}

          {/* Total Amount */}
          <div className="bg-gradient-to-br from-green-500 to-emerald-500 rounded-2xl shadow-lg p-6">
            <div className="text-white">
              <p className="text-sm mb-1 opacity-90">Total Amount</p>
              <p className="text-3xl font-bold">₹{stats.totalAmount}</p>
            </div>
          </div>
        </div>

        {/* Payments List */}
        {isLoading ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-700 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading your payment history...</p>
          </div>
        ) : payments.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <FaMoneyBillWave className="text-6xl text-gray-300 mx-auto mb-4" />
            <h3 className="text-2xl font-bold text-gray-900 mb-2">No Payments Yet</h3>
            <p className="text-gray-600">You haven't made any payments for submissions.</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Payment History</h2>
              <p className="text-sm text-gray-600 mt-1">All your payment transactions</p>
            </div>

            <div className="divide-y divide-gray-200">
              {payments.map((payment) => {
                const typeConfig = getPaymentTypeConfig(payment.type);
                const TypeIcon = typeConfig.icon;

                return (
                  <div
                    key={payment.id}
                    className={`p-6 hover:bg-gray-50 transition-all border-l-4 ${typeConfig.borderColor}`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      {/* Left Section - Payment Type */}
                      <div className="flex items-center gap-4 flex-1">
                        <div className={`w-14 h-14 ${typeConfig.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                          <TypeIcon className={`text-2xl ${typeConfig.iconColor}`} />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900">{typeConfig.label}</h3>
                          <p className="text-sm font-semibold text-indigo-600 mb-1">
                            {payment.young_india_article?.title ||
                              payment.young_india_article?.running_title?.title ||
                              (payment.purpose === 'SIGNUP_FEE' ? 'Registration / Signup Fee' : null) ||
                              (payment.purpose === 'YI_ARTICLE_FEE' ? 'Young India Article Submission' : null) ||
                              (payment.purpose === 'ABSTRACT_FEE' ? 'Abstract Submission' : null) ||
                              (payment.purpose === 'PAPER_FEE' ? 'Research Paper Submission' : null) ||
                              payment.purpose ||
                              'General Submission'}
                          </p>
                          <p className="text-xs text-gray-500">Transaction ID: {payment.razorpay_payment_id || payment.razorpay_order_id}</p>
                        </div>
                      </div>

                      {/* Middle Section - Payment Details */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                        {/* Amount */}
                        <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">Amount</p>
                          <p className="text-lg font-bold text-green-600">₹{payment.amount}</p>
                        </div>

                        {/* Payment Method */}
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">Method</p>
                          <div className="flex items-center gap-2">
                            {getPaymentMethodIcon(payment.paymentMethod)}
                            <p className="text-sm font-semibold text-gray-900 capitalize">
                              {payment.paymentMethod || 'N/A'}
                            </p>
                          </div>
                        </div>

                        {/* Date */}
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-xs text-gray-600 mb-1">Date</p>
                          <div className="flex items-center gap-2">
                            <FaCalendarAlt className="text-gray-500 text-xs" />
                            <p className="text-sm font-semibold text-gray-900">
                              {new Date(payment.date).toLocaleDateString()}
                            </p>
                          </div>
                        </div>

                        {/* Status */}
                        <div className={`${payment.status === 'PAID' ? 'bg-green-50' : 'bg-yellow-50'} p-3 rounded-lg`}>
                          <p className="text-xs text-gray-600 mb-1">Status</p>
                          <div className="flex items-center gap-2">
                            {payment.status === 'PAID' ? (
                              <FaCheckCircle className="text-green-500" />
                            ) : (
                              <FaClock className="text-yellow-600 animate-pulse" />
                            )}
                            <p className={`text-sm font-semibold ${payment.status === 'PAID' ? 'text-green-600' : 'text-yellow-700'} capitalize`}>
                              {payment.status || 'Pending'}
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Action Button for Pending */}
                      {payment.status === 'PENDING' && (
                        <div className="flex flex-col gap-2">
                          <button
                            onClick={() => handleSync(payment.razorpay_order_id)}
                            className="flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-bold hover:bg-indigo-700 transition-all shadow-md group"
                          >
                            <FaSyncAlt className="group-hover:rotate-180 transition-transform duration-500" />
                            Check Status
                          </button>
                          <p className="text-[10px] text-gray-500 text-center max-w-[120px]">
                            Paid but status not updated? Click here.
                          </p>
                        </div>
                      )}
                    </div>

                    {/* Time */}
                    <div className="mt-3 text-xs text-gray-500">
                      Paid on {new Date(payment.date).toLocaleString()}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyPayment;